/**
 * Feature flag helpers for the meridian-redesign spec.
 *
 * Validates: Requirements 13.1, 13.2, 13.3, 18.3
 *
 * Strict-equality boolean derivation from Vite env strings. Any value
 * other than the literal string "true" — including undefined, "True",
 * "1", or "false" — returns false. The helper is consumed by the
 * conditional route registration in App.tsx (task 8.8) and the
 * conditional sidebar items in Sidebar.tsx / AdminSidebar.tsx
 * (task 8.9).
 */

/**
 * Returns true only when `VITE_FEATURE_MERIDIAN_NEW_SCREENS === 'true'`.
 * Used to gate the Envoy, Patron, and Vault dashboard screens and their
 * flag-gated routes.
 *
 * NOTE: Merit was decoupled from this flag — it is now gated solely on
 * `isMeritEnabled()` so the Merit director/judge surfaces can ship to
 * production WITHOUT also exposing Envoy/Patron/Vault. This helper still
 * gates Envoy/Patron/Vault exactly as before.
 */
export function isMeridianNewScreensEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_MERIDIAN_NEW_SCREENS === 'true';
}

/**
 * Returns true only when `VITE_FEATURE_MERIT === 'true'`.
 *
 * Merit-only feature flag, fully decoupled from
 * `isMeridianNewScreensEnabled()`. It gates the Merit director surfaces
 * (`/admin/merit`, `/admin/merit/monitor`) and the standalone Merit judge
 * app (`/merit/judge` + nested routes), plus the Merit nav item in the
 * AdminSidebar. This lets Merit be enabled in production independently of
 * the Envoy/Patron/Vault meridian screens.
 *
 * Mirrors the EXACT strict-equality semantics of
 * `isMeridianNewScreensEnabled()`: any value other than the literal
 * string "true" — including undefined, "True", "1", or "false" — returns
 * false. The helper never throws and always returns a primitive boolean.
 */
export function isMeritEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_MERIT === 'true';
}
/**
 * Returns true only when `VITE_FEATURE_COMMUNITY === 'true'`.
 *
 * Community-only feature flag, fully decoupled from
 * `isMeridianNewScreensEnabled()` and `isMeritEnabled()`. It gates the
 * Phase-1 Community identity directory and detail surfaces
 * (`/admin/community`) and the Community nav item in the AdminSidebar.
 * This lets the read-only Community directory be enabled in production
 * independently of the Envoy/Patron/Vault meridian screens and the Merit
 * surfaces.
 *
 * Mirrors the EXACT strict-equality semantics of `isMeritEnabled()`: any
 * value other than the literal string "true" — including undefined,
 * "True", "1", or "false" — returns false. The helper never throws and
 * always returns a primitive boolean.
 */
export function isCommunityEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_COMMUNITY === 'true';
}

/**
 * Returns true only when `VITE_FEATURE_DURABLE_PERSON_READ === 'true'`.
 *
 * meridian-data-model Phase 2 (dual-read shims, task 4.1) flag. When OFF
 * (the default, and the safe pre-migration state) every Person read resolves
 * EXCLUSIVELY from the composed legacy view — byte-identical to the behavior
 * before this phase. When ON, the shared Person read adapter
 * (`lib/community/personReadAdapter`) prefers the durable `Person` record and
 * falls back to the legacy composed view on a miss or error, so the legacy
 * anchor always stays readable (Requirement 6.3). Flipping it back to OFF is
 * the Phase-2 rollback ("compose only").
 *
 * Mirrors the EXACT strict-equality semantics of `isCommunityEnabled()`: any
 * value other than the literal string "true" — including undefined, "True",
 * "1", or "false" — returns false. The helper never throws and always returns
 * a primitive boolean.
 */
export function isDurablePersonReadEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_DURABLE_PERSON_READ === 'true';
}

/**
 * Returns true only when `VITE_FEATURE_DOCUMENT_LIBRARY === 'true'`.
 *
 * director-document-library feature flag, fully decoupled from the other
 * feature flags. It gates the real Document Center surfaces — the director
 * browse + per-anchor library manager at `/admin/documents`
 * (`DirectorDocumentCenter`) and the contestant shared-document list at
 * `/portal/documents` (`ContestantDocumentLibrary`) — which REPLACE the legacy
 * `DocumentsPage` / `ParticipantDocuments` surfaces when the flag is on. OFF by
 * default so a deploy is inert until the backend stack is live and the flag is
 * flipped in the target environment.
 *
 * Mirrors the EXACT strict-equality semantics of `isCommunityEnabled()`: any
 * value other than the literal string "true" — including undefined, "True",
 * "1", or "false" — returns false. The helper never throws and always returns
 * a primitive boolean.
 */
export function isDocumentLibraryEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_DOCUMENT_LIBRARY === 'true';
}

/**
 * Returns true only when `VITE_FEATURE_APPLICATION_FORM === 'true'`.
 *
 * application-form-builder feature flag, fully decoupled from the other feature
 * flags. It gates the real Application_Form surfaces wired into the app shell —
 * the director Form_Builder and review/approval admin routes (and their nav
 * entries). OFF by default so a deploy is inert until the backend stack is live
 * and the flag is flipped in the target environment. Note this is independent
 * of the per-surface `VITE_USE_MOCK_APPLICATION_FORM_<SURFACE>` transport flags,
 * which choose mock-vs-live data; this flag only controls whether the real
 * routes/nav are mounted at all.
 *
 * Mirrors the EXACT strict-equality semantics of `isDocumentLibraryEnabled()`:
 * any value other than the literal string "true" — including undefined,
 * "True", "1", or "false" — returns false. The helper never throws and always
 * returns a primitive boolean.
 */
export function isApplicationFormEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_APPLICATION_FORM === 'true';
}

/**
 * Returns true only when `VITE_FEATURE_SEASON_TITLE_REGISTRY === 'true'`.
 *
 * season-scoped-title-registry feature flag, fully decoupled from the other
 * feature flags. It is the frontend twin of the backend
 * `SEASON_TITLE_REGISTRY_ENABLED` seam (read by the application-form-service
 * `titleOps` catalog validation). When ON it gates three surfaces so the deploy
 * stays inert until the production backfill seeds the season Title catalog:
 *   - the admin Settings "Titles" subpage (`TitleManagement`);
 *   - the contestant/director catalog-backed title selection
 *     (`useApplicationTitles` returns no options — free-text fallback — when
 *     OFF); and
 *   - it mirrors the backend gate on `setApprovedTitle`/`saveTitlePreferences`
 *     catalog-membership validation, so validation activates only after the
 *     backfill has run.
 * OFF by default so a deploy is inert until the backfill has run and the flag
 * is flipped in the target environment (dev-then-prod).
 *
 * Mirrors the EXACT strict-equality semantics of `isApplicationFormEnabled()`:
 * any value other than the literal string "true" — including undefined,
 * "True", "1", or "false" — returns false. The helper never throws and always
 * returns a primitive boolean.
 */
export function isSeasonTitleRegistryEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_SEASON_TITLE_REGISTRY === 'true';
}
